import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "@/lib/toast";
import { Plus, Truck, Trash2, Edit3, X } from "lucide-react";

export default function AdminSuppliers() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = async () => {
    try { const r = await api.get("/suppliers"); setRows(r.data || []); }
    catch (e) { toast.error(e?.response?.data?.detail || "Load failed"); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (s) => {
    if (!confirm(`Delete ${s.name}?`)) return;
    try { await api.delete(`/suppliers/${s.supplier_id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div data-testid="admin-suppliers" className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <p className="overline">Inventory</p>
          <h1 className="text-3xl font-heading font-medium text-[#0A2540] dark:text-white mt-1">Suppliers</h1>
          <p className="text-sm text-[#475569] dark:text-slate-400 mt-1">
            Vendors that supply your materials. Lead time is used to estimate PO ETA and reorder points.
          </p>
        </div>
        <button onClick={() => setCreating(true)} className="btn-accent text-sm inline-flex items-center gap-1" data-testid="new-supplier-btn">
          <Plus className="w-4 h-4" /> New supplier
        </button>
      </div>

      <div className="card-pb overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F8FAFC] dark:bg-slate-800 text-[10px] uppercase tracking-wider text-[#475569] dark:text-slate-400">
            <tr>
              <th className="text-left px-4 py-2">Supplier</th>
              <th className="text-left px-4 py-2">Contact</th>
              <th className="text-left px-4 py-2">Lead time</th>
              <th className="text-left px-4 py-2">Terms</th>
              <th className="px-4 py-2 w-24"></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={5} className="text-center py-10 text-[#94A3B8] italic">
                No suppliers yet. Click "New supplier" to add one.
              </td></tr>
            )}
            {rows.map((s) => (
              <tr key={s.supplier_id} data-testid={`supplier-row-${s.supplier_id}`}
                  className="border-t border-[#E2E8F0] dark:border-slate-700 hover:bg-[#F8FAFC] dark:hover:bg-slate-800">
                <td className="px-4 py-2">
                  <div className="font-medium text-[#0A2540] dark:text-white inline-flex items-center gap-1.5">
                    <Truck className="w-3.5 h-3.5 text-[#C9A356]" /> {s.name}
                  </div>
                  {!s.is_active && <span className="text-[10px] text-red-500 uppercase tracking-wider ml-2">inactive</span>}
                </td>
                <td className="px-4 py-2 text-[#475569] dark:text-slate-400">
                  {s.contact_name || <span className="italic text-[#94A3B8]">—</span>}
                  {s.email && <div className="text-[10px]">{s.email}</div>}
                </td>
                <td className="px-4 py-2 tabular-nums">{s.lead_time_days}d</td>
                <td className="px-4 py-2">{s.terms}</td>
                <td className="px-4 py-2 text-right whitespace-nowrap">
                  <button onClick={() => setEditing(s)} className="text-[#475569] dark:text-slate-400 hover:text-[#0A2540] dark:hover:text-white p-1" data-testid={`edit-supplier-${s.supplier_id}`}>
                    <Edit3 className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => remove(s)} className="text-red-500 hover:text-red-700 p-1" data-testid={`del-supplier-${s.supplier_id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {(creating || editing) && (
        <SupplierModal
          initial={editing}
          onClose={() => { setCreating(false); setEditing(null); }}
          onSaved={() => { setCreating(false); setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function SupplierModal({ initial, onClose, onSaved }) {
  const [name, setName] = useState(initial?.name || "");
  const [contact, setContact] = useState(initial?.contact_name || "");
  const [email, setEmail] = useState(initial?.email || "");
  const [phone, setPhone] = useState(initial?.phone || "");
  const [address, setAddress] = useState(initial?.address || "");
  const [terms, setTerms] = useState(initial?.terms || "Net 30");
  const [lead, setLead] = useState(initial?.lead_time_days || 14);
  const [notes, setNotes] = useState(initial?.notes || "");
  const [isActive, setIsActive] = useState(initial?.is_active ?? true);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!name.trim()) { toast.error("Name required"); return; }
    setSaving(true);
    try {
      const body = {
        name, contact_name: contact, email, phone, address,
        terms, lead_time_days: Number(lead), notes, is_active: isActive,
      };
      if (initial) await api.patch(`/suppliers/${initial.supplier_id}`, body);
      else await api.post("/suppliers", body);
      toast.success("Saved");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const cls = "w-full border border-[#E2E8F0] dark:border-slate-700 dark:bg-slate-800 dark:text-white rounded-md px-3 py-1.5 text-sm";

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4" onClick={onClose} role="dialog">
      <div className="bg-white dark:bg-slate-900 rounded-xl p-5 max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-lg text-[#0A2540] dark:text-white">
            {initial ? "Edit supplier" : "New supplier"}
          </h3>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="text-[10px] uppercase font-bold text-[#94A3B8]">Name *</label>
            <input className={cls} value={name} onChange={(e) => setName(e.target.value)} data-testid="supplier-name" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] uppercase font-bold text-[#94A3B8]">Contact person</label>
              <input className={cls} value={contact} onChange={(e) => setContact(e.target.value)} data-testid="supplier-contact" />
            </div>
            <div>
              <label className="text-[10px] uppercase font-bold text-[#94A3B8]">Email</label>
              <input className={cls} type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="supplier-email" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] uppercase font-bold text-[#94A3B8]">Phone</label>
              <input className={cls} value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="supplier-phone" />
            </div>
            <div>
              <label className="text-[10px] uppercase font-bold text-[#94A3B8]">Lead time (days)</label>
              <input className={cls} type="number" min="1" value={lead} onChange={(e) => setLead(e.target.value)} data-testid="supplier-lead" />
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase font-bold text-[#94A3B8]">Address</label>
            <input className={cls} value={address} onChange={(e) => setAddress(e.target.value)} />
          </div>
          <div>
            <label className="text-[10px] uppercase font-bold text-[#94A3B8]">Payment terms</label>
            <input className={cls} value={terms} onChange={(e) => setTerms(e.target.value)} />
          </div>
          <div>
            <label className="text-[10px] uppercase font-bold text-[#94A3B8]">Notes</label>
            <textarea className={cls} rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>
          {initial && (
            <label className="text-xs inline-flex items-center gap-1.5 text-[#475569] dark:text-slate-400">
              <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
              Active
            </label>
          )}
          <button onClick={save} disabled={saving} className="btn-accent text-sm w-full disabled:opacity-50" data-testid="supplier-save">
            {saving ? "Saving…" : "Save supplier"}
          </button>
        </div>
      </div>
    </div>
  );
}
